import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { profileApi, assignmentsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  AlertTriangle, CheckCircle2, ClipboardList, Download, Eye, FileText, Info, Loader2, Mail, ShieldX, User as UserIcon, X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return iso;
  }
};

/**
 * Read-only viewer for an assignee's required documents.
 *
 * Privileged staff (Admin, CRD, Chairman, Coordinator, Director, Exec Director)
 *   → see Download + "Send urgent email" buttons.
 * Self-view (a user viewing their own row):
 *   → READ-ONLY summary only. No upload/download/email buttons.
 *     A notice directs the user to their account profile to manage documents.
 */
export function AssigneeDocumentsDialog({ requestId, assignee, open, onOpenChange, viewerIsSelf = false, onReload }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ user: null, documents: [] });
  const [downloadingId, setDownloadingId] = useState(null);
  const [sendingDocType, setSendingDocType] = useState(null);
  const [sentDocTypes, setSentDocTypes] = useState(new Set()); // session-disable per doc_type
  // iter119 — inline lightweight preview state: map doc_id → { url, contentType, loading }
  const [previewByDoc, setPreviewByDoc] = useState({});
  // iter121 — assigned-tasks tab state (default to TASKS since coordinators typically
  // open this dialog to see what they owe on a request, not just doc compliance).
  const [activeTab, setActiveTab] = useState('tasks');
  const [tasksData, setTasksData] = useState(null);
  const [tasksLoading, setTasksLoading] = useState(false);

  const reload = async () => {
    if (!requestId || !assignee?.id) return;
    setLoading(true);
    try {
      const res = await profileApi.getAssigneeDocuments(requestId, assignee.id);
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) toast.error('You are not allowed to view this user\'s documents');
      else toast.error('Failed to load documents');
      setData({ user: null, documents: [] });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSentDocTypes(new Set());
    // iter119 — clear stale preview URLs whenever the dialog opens for a new assignee.
    setPreviewByDoc((prev) => {
      Object.values(prev).forEach((p) => {
        if (p?.url) {
          try { URL.revokeObjectURL(p.url); } catch (e) { /* noop */ }
        }
      });
      return {};
    });
    // iter121/122 — open on TASKS tab by default and clear stale data on each open
    setActiveTab('tasks');
    setTasksData(null);
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requestId, assignee?.id]);

  // iter121 — lazy-load Assigned Tasks the first time the tab is selected.
  useEffect(() => {
    if (!open || activeTab !== 'tasks' || tasksData || !assignee?.id || !requestId) return;
    let cancelled = false;
    (async () => {
      setTasksLoading(true);
      try {
        const res = await assignmentsApi.getAssigneeTasks(requestId, assignee.id);
        if (!cancelled) setTasksData(res.data);
      } catch {
        if (!cancelled) toast.error("Couldn't load assigned tasks");
      } finally {
        if (!cancelled) setTasksLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, activeTab, tasksData, assignee?.id, requestId]);

  // iter119 — inline preview toggle. Fetches the file as a blob and creates an
  // object URL. PDFs → <iframe>, images → <img>, anything else → "not previewable".
  const handleTogglePreview = async (doc) => {
    if (!doc.doc_id) return;
    const existing = previewByDoc[doc.doc_id];
    if (existing && existing.url) {
      // Already loaded → toggle close
      try { URL.revokeObjectURL(existing.url); } catch (e) { /* noop */ }
      setPreviewByDoc((prev) => {
        const next = { ...prev };
        delete next[doc.doc_id];
        return next;
      });
      return;
    }
    setPreviewByDoc((prev) => ({ ...prev, [doc.doc_id]: { loading: true } }));
    try {
      const res = await profileApi.downloadProfileDocument(doc.doc_id);
      const blob = res.data;
      const contentType = blob?.type || res.headers?.['content-type'] || '';
      const url = URL.createObjectURL(blob);
      setPreviewByDoc((prev) => ({ ...prev, [doc.doc_id]: { url, contentType, loading: false } }));
    } catch {
      toast.error('Could not load preview');
      setPreviewByDoc((prev) => {
        const next = { ...prev };
        delete next[doc.doc_id];
        return next;
      });
    }
  };

  const handleDownload = async (doc) => {
    if (!doc.doc_id) return;
    setDownloadingId(doc.doc_id);
    try {
      const res = await profileApi.downloadProfileDocument(doc.doc_id);
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.filename || `${doc.doc_type}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Could not download file');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleSendUrgent = async (doc) => {
    setSendingDocType(doc.doc_type);
    try {
      await profileApi.sendUrgentDocEmail(requestId, assignee.id, doc.doc_type);
      toast.success(`Urgent email sent for ${doc.doc_type}`);
      setSentDocTypes((prev) => new Set(prev).add(doc.doc_type));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error(detail || 'An urgent email was already sent in the last 24h.');
      } else {
        toast.error(detail || 'Failed to send urgent email');
      }
    } finally {
      setSendingDocType(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="assignee-docs-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserIcon className="w-5 h-5 text-blue-700" />
            {/* iter122 — dialog title now reflects the active tab so users can tell
                whether they're looking at docs or tasks at a glance. */}
            {assignee?.name || 'Assignee'}
            {' — '}
            {activeTab === 'tasks'
              ? `Assigned Tasks${tasksData?.counts?.total ? ` (${tasksData.counts.total})` : ''}`
              : 'Required Documents'}
            {viewerIsSelf && <Badge className="ml-2 bg-blue-100 text-blue-700">Your documents</Badge>}
          </DialogTitle>
          <DialogDescription>
            Read-only view. <span className="font-medium text-slate-700">{(data.user?.role) || assignee?.role || ''}</span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
          </div>
        ) : (
          // iter121 — two-tab layout: Required Documents + Assigned Tasks
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full" data-testid="assignee-dialog-tabs">
            <TabsList className="grid w-full grid-cols-2 mb-3">
              <TabsTrigger value="documents" data-testid="assignee-dialog-tab-documents" className="gap-1.5">
                <FileText className="w-3.5 h-3.5" />
                Required Documents
              </TabsTrigger>
              <TabsTrigger value="tasks" data-testid="assignee-dialog-tab-tasks" className="gap-1.5">
                <ClipboardList className="w-3.5 h-3.5" />
                Assigned Tasks
                {tasksData?.counts?.total > 0 && (
                  <Badge className="ml-1 bg-blue-900 text-white text-[10px] px-1 py-0">
                    {tasksData.counts.total}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="documents" className="mt-0">
              <div className="space-y-3" data-testid="assignee-docs-list">
            {viewerIsSelf && (
              <div
                className="flex items-start gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg"
                data-testid="self-view-profile-notice"
              >
                <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-blue-900">
                  This is a read-only summary of your required documents for this request.
                  To upload, replace, or update any document, please go to your{' '}
                  <Link
                    to="/profile"
                    onClick={() => onOpenChange(false)}
                    className="font-semibold underline hover:text-blue-700"
                    data-testid="self-view-profile-link"
                  >
                    Account Profile
                  </Link>.
                </div>
              </div>
            )}
            {data.documents.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No required documents are configured for this user's role on this request type.</p>
              </div>
            ) : (
              data.documents.map((d) => {
                const dueColor = !d.uploaded && d.severity === 'block'
                  ? 'bg-red-50 border-red-200'
                  : !d.uploaded
                    ? 'bg-amber-50 border-amber-200'
                    : d.expired
                      ? 'bg-red-50 border-red-200'
                      : d.days_until_expiry != null && d.days_until_expiry <= 30
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-slate-50 border-slate-200';
                const sent = sentDocTypes.has(d.doc_type);
                const sending = sendingDocType === d.doc_type;
                const needsAttention =
                  !d.uploaded ||
                  d.expired ||
                  (d.days_until_expiry != null && d.days_until_expiry <= 30);
                return (
                  <div
                    key={d.doc_type}
                    className={`p-3 rounded-lg border ${dueColor}`}
                    data-testid={`assignee-doc-${d.doc_type}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-0.5">
                        {!d.uploaded ? (
                          <ShieldX className={`w-5 h-5 ${d.severity === 'block' ? 'text-red-600' : 'text-amber-600'}`} />
                        ) : d.expired ? (
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        ) : d.days_until_expiry != null && d.days_until_expiry <= 30 ? (
                          <AlertTriangle className="w-5 h-5 text-amber-600" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">{d.doc_type}</span>
                          {d.required && <Badge className="bg-slate-100 text-slate-600 text-[10px]">Required</Badge>}
                          {!d.uploaded && d.severity === 'block' && (
                            <Badge className="bg-red-100 text-red-700 text-[10px]">Blocks assignment</Badge>
                          )}
                          {!d.uploaded && d.severity !== 'block' && (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]">Missing</Badge>
                          )}
                          {d.uploaded && d.expired && <Badge className="bg-red-100 text-red-700 text-[10px]">Expired</Badge>}
                          {d.uploaded && !d.expired && d.days_until_expiry != null && d.days_until_expiry <= 30 && (
                            <Badge className="bg-amber-100 text-amber-700 text-[10px]">
                              Expires in {d.days_until_expiry} day{d.days_until_expiry === 1 ? '' : 's'}
                            </Badge>
                          )}
                          {d.uploaded && !d.expired && d.days_until_expiry != null && d.days_until_expiry > 30 && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Valid</Badge>
                          )}
                        </div>
                        {d.uploaded ? (
                          <p className="text-xs text-slate-500 mt-1 truncate">
                            {d.filename}
                            {d.uploaded_at && <span> · Uploaded {formatDate(d.uploaded_at)}</span>}
                            {d.expires_at && <span> · Expires {formatDate(d.expires_at)}</span>}
                          </p>
                        ) : (
                          <p className="text-xs text-slate-500 mt-1 italic">
                            {viewerIsSelf ? 'Not yet uploaded — manage it from your Account Profile.' : 'Not yet uploaded by the user'}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {/* iter119 — Preview button (lightweight inline embed). Available for
                            both privileged staff and self-viewers. Toggles open/close. */}
                        {d.uploaded && d.doc_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleTogglePreview(d)}
                            disabled={previewByDoc[d.doc_id]?.loading}
                            data-testid={`assignee-doc-preview-${d.doc_type}`}
                            title={previewByDoc[d.doc_id]?.url ? 'Hide preview' : 'Preview inline'}
                          >
                            {previewByDoc[d.doc_id]?.loading ? (
                              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            ) : previewByDoc[d.doc_id]?.url ? (
                              <X className="w-4 h-4 mr-1.5" />
                            ) : (
                              <Eye className="w-4 h-4 mr-1.5" />
                            )}
                            {previewByDoc[d.doc_id]?.url ? 'Hide' : 'Preview'}
                          </Button>
                        )}
                        {/* Privileged staff: download button */}
                        {!viewerIsSelf && d.uploaded && d.doc_id && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDownload(d)}
                            disabled={downloadingId === d.doc_id}
                            data-testid={`assignee-doc-download-${d.doc_type}`}
                          >
                            {downloadingId === d.doc_id ? (
                              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4 mr-1.5" />
                            )}
                            Download
                          </Button>
                        )}
                        {/* Privileged staff: urgent email button (NOT self) */}
                        {!viewerIsSelf && needsAttention && (
                          <Button
                            size="sm"
                            variant={d.uploaded ? 'ghost' : 'default'}
                            onClick={() => handleSendUrgent(d)}
                            disabled={sending || sent}
                            className={d.uploaded ? '' : 'bg-amber-600 hover:bg-amber-700'}
                            data-testid={`assignee-doc-urgent-${d.doc_type}`}
                            title={sent ? 'Sent in this session' : `Email ${assignee?.name} to upload/refresh ${d.doc_type}`}
                          >
                            {sending ? (
                              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                            ) : (
                              <Mail className="w-4 h-4 mr-1.5" />
                            )}
                            {sent ? 'Sent' : 'Send urgent email'}
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* iter119 — inline lightweight preview block. PDF → iframe, image → img,
                        everything else → fallback link to download. Rendered below the row so
                        the metadata is still visible above. */}
                    {previewByDoc[d.doc_id]?.url && (
                      <div className="mt-3 border border-slate-200 rounded-md overflow-hidden bg-white" data-testid={`assignee-doc-preview-pane-${d.doc_type}`}>
                        {(() => {
                          const p = previewByDoc[d.doc_id];
                          const ct = (p.contentType || '').toLowerCase();
                          if (ct.includes('pdf') || (d.filename || '').toLowerCase().endsWith('.pdf')) {
                            return (
                              <iframe
                                src={p.url}
                                title={d.filename}
                                className="w-full"
                                style={{ height: 480, border: 0 }}
                              />
                            );
                          }
                          if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(d.filename || '')) {
                            return (
                              <img
                                src={p.url}
                                alt={d.filename}
                                className="max-h-[480px] w-full object-contain bg-slate-50"
                              />
                            );
                          }
                          return (
                            <div className="p-4 text-center text-sm text-slate-600">
                              Inline preview isn't available for this file type. Please use{' '}
                              <button
                                type="button"
                                onClick={() => handleDownload(d)}
                                className="underline font-medium text-blue-700 hover:text-blue-900"
                              >
                                Download
                              </button>{' '}
                              to open it locally.
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>
                );
              })
            )}
              </div>
            </TabsContent>

            <TabsContent value="tasks" className="mt-0">
              {tasksLoading ? (
                <div className="py-12 flex items-center justify-center" data-testid="assignee-tasks-loading">
                  <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
                </div>
              ) : !tasksData ? (
                <div className="text-center py-8 text-slate-500">
                  <ClipboardList className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Loading tasks…</p>
                </div>
              ) : tasksData.counts.total === 0 ? (
                <div className="text-center py-8 text-slate-500" data-testid="assignee-tasks-empty">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-2 text-emerald-300" />
                  <p className="text-sm">No open tasks on this request.</p>
                </div>
              ) : (
                <div className="space-y-4" data-testid="assignee-tasks-list">
                  {tasksData.doc_threads.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" />
                        Document threads pending review · {tasksData.doc_threads.length}
                      </div>
                      <div className="space-y-2">
                        {tasksData.doc_threads.map((t) => (
                          <div
                            key={t.document_id}
                            className="p-3 rounded-lg border bg-blue-50 border-blue-200"
                            data-testid={`assignee-task-doc-${t.document_id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-slate-900 truncate">{t.filename}</div>
                                <div className="text-[11px] text-slate-600 mt-0.5">
                                  {t.doc_type} · v{t.version}
                                </div>
                              </div>
                              <Badge className={`text-[10px] ${t.status === 'APPROVED' ? 'bg-emerald-100 text-emerald-800' : t.status === 'CHANGES_REQUESTED' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'}`}>
                                {(t.status || 'PENDING').replace(/_/g, ' ')}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {tasksData.pending_approvals.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Pending approvals · {tasksData.pending_approvals.length}
                      </div>
                      <div className="space-y-2">
                        {tasksData.pending_approvals.map((a) => (
                          <div
                            key={a.approval_id}
                            className="p-3 rounded-lg border bg-purple-50 border-purple-200"
                            data-testid={`assignee-task-approval-${a.approval_id}`}
                          >
                            <div className="text-sm font-medium text-slate-900">
                              Stage: {a.stage || 'Approval'}
                            </div>
                            <div className="text-[11px] text-slate-600 mt-0.5">
                              Scope: {a.scope || 'request'}{a.created_at ? ` · Opened ${formatDate(a.created_at)}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {tasksData.pending_assignments.length > 0 && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                        <UserIcon className="w-3.5 h-3.5" />
                        Pending assignment confirmations · {tasksData.pending_assignments.length}
                      </div>
                      <div className="space-y-2">
                        {tasksData.pending_assignments.map((a) => (
                          <div
                            key={a.assignment_id}
                            className="p-3 rounded-lg border bg-amber-50 border-amber-200"
                            data-testid={`assignee-task-assignment-${a.assignment_id}`}
                          >
                            <div className="text-sm font-medium text-slate-900">
                              Role: {a.assignment_role}
                            </div>
                            <div className="text-[11px] text-slate-600 mt-0.5">
                              Status: {a.status}{a.due_date ? ` · Due ${formatDate(a.due_date)}` : ''}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* iter122 — coordinator action items (e.g. "Assign Investigators",
                      "Assign Reviewer(s)"). Surfaced only for the active coordinator on
                      the request via the backend pending_actions list. */}
                  {(tasksData.pending_actions || []).length > 0 && (
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2 flex items-center gap-1.5">
                        <ClipboardList className="w-3.5 h-3.5" />
                        Coordinator actions needed · {tasksData.pending_actions.length}
                      </div>
                      <div className="space-y-2">
                        {tasksData.pending_actions.map((act) => (
                          <div
                            key={act.action_key}
                            className="p-3 rounded-lg border bg-rose-50 border-rose-200"
                            data-testid={`assignee-task-action-${act.action_key}`}
                          >
                            <div className="text-sm font-medium text-rose-900">
                              {act.label}
                            </div>
                            <div className="text-[11px] text-rose-700 mt-0.5">
                              {act.hint}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default AssigneeDocumentsDialog;
